import React, { useEffect, useState } from 'react';
import { FileText, ShoppingCart, Package, CheckCircle2, Clock, XCircle, ArrowRight, AlertTriangle, Loader2 } from 'lucide-react';
import { documentTrailAPI } from '../../services/api';

const STATUS_CONFIG = {
  pending_approval: { label: 'Pending Approval', color: 'text-amber-600', bg: 'bg-amber-50', step: 0 },
  rejected: { label: 'Rejected', color: 'text-red-600', bg: 'bg-red-50', step: 0 },
  approved_awaiting_po: { label: 'Approved — Awaiting PO', color: 'text-blue-600', bg: 'bg-blue-50', step: 1 },
  po_issued: { label: 'PO Issued', color: 'text-indigo-600', bg: 'bg-indigo-50', step: 2 },
  in_transit: { label: 'In Transit', color: 'text-purple-600', bg: 'bg-purple-50', step: 2 },
  partially_received: { label: 'Partially Received', color: 'text-orange-600', bg: 'bg-orange-50', step: 3 },
  completed: { label: 'Completed', color: 'text-emerald-600', bg: 'bg-emerald-50', step: 4 },
  cancelled: { label: 'Cancelled', color: 'text-gray-500', bg: 'bg-gray-100', step: -1 },
  in_progress: { label: 'In Progress', color: 'text-blue-600', bg: 'bg-blue-50', step: 2 },
  not_found: { label: 'Not Found', color: 'text-gray-400', bg: 'bg-gray-50', step: -1 },
};

const STEPS = [
  { key: 'indent', label: 'Indent', icon: FileText },
  { key: 'approval', label: 'Approval', icon: CheckCircle2 },
  { key: 'po', label: 'Purchase Order', icon: ShoppingCart },
  { key: 'grn', label: 'Goods Received', icon: Package },
  { key: 'complete', label: 'Completed', icon: CheckCircle2 },
];

export const DocumentTrail = ({ docType, docId, compact = false }) => {
  const [trail, setTrail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!docType || !docId) return;
    setLoading(true);
    documentTrailAPI.getTrail(docType, docId)
      .then(setTrail)
      .catch(() => setTrail(null))
      .finally(() => setLoading(false));
  }, [docType, docId]);

  if (loading) return <div className="flex items-center gap-2 py-3 text-xs text-gray-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading trail...</div>;
  if (!trail) return null;

  const lifecycle = STATUS_CONFIG[trail.lifecycle_status] || STATUS_CONFIG.not_found;
  const activeStep = lifecycle.step;

  if (compact) {
    return (
      <div data-testid="document-trail-compact" className="flex items-center gap-1.5 flex-wrap">
        {STEPS.map((step, idx) => {
          const done = activeStep >= idx;
          const active = activeStep === idx;
          const cancelled = activeStep === -1;
          return (
            <React.Fragment key={step.key}>
              {idx > 0 && <ArrowRight className={`w-3 h-3 ${done ? 'text-emerald-400' : 'text-gray-300'}`} />}
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                cancelled ? 'bg-gray-100 text-gray-400' :
                active ? `${lifecycle.bg} ${lifecycle.color}` :
                done ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-50 text-gray-400'
              }`}>
                <step.icon className="w-2.5 h-2.5" />
                {step.label}
              </span>
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  return (
    <div data-testid="document-trail" className="bg-white border border-gray-100 rounded-xl p-4 space-y-4">
      {/* Lifecycle Status Badge */}
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Document Trail</h4>
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold ${lifecycle.bg} ${lifecycle.color}`}>
          {lifecycle.step === 4 ? <CheckCircle2 className="w-3.5 h-3.5" /> :
           lifecycle.step === -1 ? <XCircle className="w-3.5 h-3.5" /> :
           <Clock className="w-3.5 h-3.5" />}
          {lifecycle.label}
        </span>
      </div>

      {/* Pipeline Steps */}
      <div className="flex items-center gap-0">
        {STEPS.map((step, idx) => {
          const done = activeStep >= idx;
          const active = activeStep === idx;
          const cancelled = activeStep === -1;
          return (
            <React.Fragment key={step.key}>
              <div className={`flex flex-col items-center flex-1 ${idx === 0 ? '' : ''}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${
                  cancelled ? 'border-gray-200 bg-gray-50' :
                  done ? 'border-emerald-500 bg-emerald-50' :
                  active ? `border-current ${lifecycle.bg}` : 'border-gray-200 bg-gray-50'
                }`}>
                  <step.icon className={`w-4 h-4 ${
                    cancelled ? 'text-gray-300' :
                    done ? 'text-emerald-600' :
                    active ? lifecycle.color : 'text-gray-300'
                  }`} />
                </div>
                <span className={`text-[10px] mt-1.5 font-medium text-center leading-tight ${
                  cancelled ? 'text-gray-400' :
                  done ? 'text-emerald-700' :
                  active ? lifecycle.color : 'text-gray-400'
                }`}>{step.label}</span>
              </div>
              {idx < STEPS.length - 1 && (
                <div className={`h-0.5 flex-1 -mt-4 ${
                  cancelled ? 'bg-gray-200' :
                  activeStep > idx ? 'bg-emerald-400' : 'bg-gray-200'
                }`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Document Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
        {/* Indent Card */}
        <div className={`rounded-lg border p-3 ${trail.indent ? 'border-gray-200 bg-white' : 'border-dashed border-gray-200 bg-gray-50'}`}>
          <div className="flex items-center gap-2 mb-2">
            <FileText className={`w-4 h-4 ${trail.indent ? 'text-blue-600' : 'text-gray-300'}`} />
            <span className="text-[11px] font-semibold text-gray-700">Indent</span>
          </div>
          {trail.indent ? (
            <div className="space-y-1">
              <p className="text-xs font-medium text-gray-900">{trail.indent.indent_number}</p>
              <p className="text-[10px] text-gray-500">{trail.indent.purpose}</p>
              <span className={`inline-block px-2 py-0.5 rounded text-[9px] font-semibold ${
                trail.indent.status === 'Fulfilled' ? 'bg-emerald-50 text-emerald-700' :
                trail.indent.status === 'Approved' ? 'bg-blue-50 text-blue-700' :
                trail.indent.status === 'PO Issued' ? 'bg-indigo-50 text-indigo-700' :
                'bg-gray-100 text-gray-600'
              }`}>{trail.indent.status}</span>
            </div>
          ) : (
            <p className="text-[10px] text-gray-400 italic">No linked indent</p>
          )}
        </div>

        {/* PO Card */}
        <div className={`rounded-lg border p-3 ${trail.po ? 'border-gray-200 bg-white' : 'border-dashed border-gray-200 bg-gray-50'}`}>
          <div className="flex items-center gap-2 mb-2">
            <ShoppingCart className={`w-4 h-4 ${trail.po ? 'text-indigo-600' : 'text-gray-300'}`} />
            <span className="text-[11px] font-semibold text-gray-700">Purchase Order</span>
          </div>
          {trail.po ? (
            <div className="space-y-1">
              <p className="text-xs font-medium text-gray-900">{trail.po.po_number}</p>
              <p className="text-[10px] text-gray-500">{trail.po.vendor_name}</p>
              <div className="flex items-center gap-2">
                <span className={`inline-block px-2 py-0.5 rounded text-[9px] font-semibold ${
                  trail.po.status === 'Received' ? 'bg-emerald-50 text-emerald-700' :
                  trail.po.status === 'Partially Received' ? 'bg-orange-50 text-orange-700' :
                  trail.po.status === 'Ordered' ? 'bg-blue-50 text-blue-700' :
                  'bg-gray-100 text-gray-600'
                }`}>{trail.po.status}</span>
                {trail.po.amount > 0 && <span className="text-[9px] text-gray-500">{trail.po.currency || 'INR'} {Number(trail.po.amount).toLocaleString()}</span>}
              </div>
            </div>
          ) : (
            <p className="text-[10px] text-gray-400 italic">Not yet created</p>
          )}
        </div>

        {/* GRN Card */}
        <div className={`rounded-lg border p-3 ${trail.grns?.length > 0 ? 'border-gray-200 bg-white' : 'border-dashed border-gray-200 bg-gray-50'}`}>
          <div className="flex items-center gap-2 mb-2">
            <Package className={`w-4 h-4 ${trail.grns?.length > 0 ? 'text-emerald-600' : 'text-gray-300'}`} />
            <span className="text-[11px] font-semibold text-gray-700">Goods Received ({trail.grns?.length || 0})</span>
          </div>
          {trail.grns?.length > 0 ? (
            <div className="space-y-1 max-h-24 overflow-y-auto">
              {trail.grns.map((g, i) => (
                <div key={g.id} className="flex items-center justify-between text-[10px]">
                  <span className="text-gray-700 truncate">{g.product_name}</span>
                  <span className="text-gray-500 whitespace-nowrap ml-2">{g.quantity} {g.unit}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[10px] text-gray-400 italic">No inward entries yet</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentTrail;
